<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      The final aspect of fitness that we’ll look at is strength training, a vital element in
      creating a balanced training programme.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5 text-bold">Why is it so important?</p>
        <p class="mb-5">
          Not only does strength training help clients manage or lose weight, it also increases the
          metabolism which helps burn more calories.
        </p>
        <p class="mb-5">
          Additionally strength training can enhance quality of life and improve a person’s ability
          to do everyday activities.
        </p>
        <p class="mb-5">
          This style of training also plays an important role in protecting joints from injury and
          fatigue.
        </p>
        <p class="mb-5">So you can see how strength training has many benefits for clients!</p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/fitness/strength_training.jpg"
          alt=""
          class="mx-auto img-responsive"
        />
      </div>
    </div>

    <h4 class="mb-5">How To Use Strength Training</h4>
    <p class="mb-5">
      When we think of strength training, we usually imagine people pumping weights or using weight
      machines - but most of us don’t have access to these things at home.
    </p>
    <p class="mb-5">
      That’s why as a trainer it’s important to be able to adapt to the client’s situation and use
      alternative methods they can use at home.
    </p>
    <p class="mb-5">
      You’ll often set a client ‘homework’, so finding imaginative ways of them being able to
      workout can be a fun yet rewarding challenge - like using their body to improve strength.
    </p>
    <p class="mb-5">
      That’s where bodyweight exercises come in. They allow the client to workout specific muscles
      without the need for specialist (and expensive!) equipment.
    </p>
    <p class="mb-5">
      To get some ideas for bodyweight exercises that require minimal equipment, watch this video:
    </p>

    <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].url" />
    <p class="mb-5">
      From the exercises in the video above (or from your own knowledge or research), choose one
      exercise to work on each muscle group. Write it in the space below.
    </p>
    <p class="mb-5">Complete the workout by seeing how many reps you can do for each exercise.</p>
    <p class="mb-5">Record the number of reps for each exercise in the table too.</p>

    <table class="table default-table">
      <tr class="header">
        <th colspan="4">MY BODYWEIGHT STRENGTH ROUTINE</th>
      </tr>
      <tr class="subheader">
        <th scope="col" width="40%" colspan="2">MUSCLE GROUP</th>
        <th scope="col" width="30%">EXERCISE</th>
        <th scope="col" width="30%">MAX No. Of REPS</th>
      </tr>
      <tr class="body" v-for="(group, index) in muscle_groups" :key="'mg_1_' + index">
        <th scope="row" class="subheader">{{ index + 1 }}</th>
        <td>{{ group }}</td>
        <td><text-block v-model="data['question_1_1_' + index]" :editable="isEditable" /></td>
        <td><text-block v-model="data['question_1_2_' + index]" :editable="isEditable" /></td>
      </tr>
    </table>

    <p class="mb-5">
      Use your self-designed strength training routine every day for a week then take 2 days rest.
    </p>
    <p class="mb-5">
      Finally, work your way through the routine one more time, recording your maximum rep as you do
      it.
    </p>
    <p class="mb-5">
      Have you been able to increase your maximum number of reps for each exercise?
    </p>
    <p class="mb-5">Record your results in the table below.</p>

    <table class="table default-table">
      <tr class="header">
        <th colspan="4">MY BODYWEIGHT STRENGTH ROUTINE</th>
      </tr>
      <tr class="subheader">
        <th scope="col" width="40%" colspan="2">MUSCLE GROUP</th>
        <th scope="col" width="30%">EXERCISE</th>
        <th scope="col" width="30%">MAX No. Of REPS</th>
      </tr>
      <tr class="body" v-for="(group, index) in muscle_groups" :key="'mg_2_' + index">
        <th scope="row" class="subheader">{{ index + 1 }}</th>
        <td>{{ group }}</td>
        <td><text-block v-model="data['question_2_1_' + index]" :editable="isEditable" /></td>
        <td><text-block v-model="data['question_2_2_' + index]" :editable="isEditable" /></td>
      </tr>
    </table>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/strength_video_splash.jpg'),
          url: 'https://www.youtube.com/embed/A-B3GIPWH7g',
        },
      },
      muscle_groups: [
        'Back',
        'Chest',
        'Shoulders',
        'Biceps',
        'Triceps',
        'Abs',
        'Glutes',
        'Hamstrings',
        'Quads',
        'Calves',
      ],
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>
